import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #addonVariantCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Buscar si esta línea pertenece a un bundle (garantía extendida)
    const productRow = this.refs.cartItemRows[line - 1];
    const bundleId = productRow?.dataset.bundleId;
    if (bundleId) {
      return this.#updateBundleQuantities(line, quantity, bundleId);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Actualiza las cantidades de todas las líneas de un bundle (producto + garantía).
   * Usa /cart/change.js secuencialmente para cada línea (mismo endpoint que updateQuantity).
   * @param {number} productLine - Línea del producto principal (1-based)
   * @param {number} quantity - Nueva cantidad
   * @param {string} bundleId - ID del bundle
   */
  #updateBundleQuantities(productLine, quantity, bundleId) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('change:user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const sectionsStr = Array.from(sectionsToUpdate).join(',');

    // Encontrar todas las líneas del bundle en orden
    const bundleLines = [productLine];
    const rows = this.refs.cartItemRows;
    for (let i = 0; i < rows.length; i++) {
      if (i !== productLine - 1 && rows[i].dataset.bundleId === bundleId) {
        bundleLines.push(i + 1);
      }
    }

    cartTotal?.shimmer();

    // Marcar shimmer en las filas del bundle
    for (const line of bundleLines) {
      const row = rows[line - 1];
      if (row) {
        const textComponent = /** @type {TextComponent | undefined} */ (row.querySelector('text-component'));
        textComponent?.shimmer();
      }
    }

    // Llamadas secuenciales a /cart/change.js para cada línea del bundle
    this.#changeBundleLines(bundleLines, quantity, sectionsStr, 0)
      .then((lastResponse) => {
        if (!lastResponse) return;

        resetShimmer(this);

        if (lastResponse.errors) {
          this.#handleCartError(productLine, lastResponse);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          lastResponse.sections?.[this.sectionId] || '',
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(lastResponse);

        this.dispatchEvent(
          new CartUpdateEvent(lastResponse, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: lastResponse.sections,
          })
        );

        if (lastResponse.sections?.[this.sectionId]) {
          morphSection(this.sectionId, lastResponse.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
        }

        void this.#syncProtectedPurchaseFromCart(lastResponse);

        this.#updateCartQuantitySelectorButtonStates();
        this.#notifyDrawerAfterMorph();
      })
      .catch((error) => {
        console.error('[bundle] Error updating bundle quantities:', error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Cambia secuencialmente las líneas de un bundle usando /cart/change.js.
   * @param {number[]} lines - Array de números de línea (1-based)
   * @param {number} quantity - Nueva cantidad
   * @param {string} sectionsStr - Secciones a actualizar
   * @param {number} index - Índice actual en el array
   * @returns {Promise<Object|null>} La respuesta del último cambio
   */
  #changeBundleLines(lines, quantity, sectionsStr, index) {
    if (index >= lines.length) return Promise.resolve(null);

    const body = JSON.stringify({
      line: lines[index],
      quantity,
      sections: sectionsStr,
      sections_url: window.location.pathname,
    });

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = JSON.parse(responseText);

        if (parsed.errors || index === lines.length - 1) {
          // Error o última línea: devolver esta respuesta
          return parsed;
        }

        // Continuar con la siguiente línea
        return this.#changeBundleLines(lines, quantity, sectionsStr, index + 1);
      });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    // Buscar líneas del mismo bundle (garantía extendida) para eliminar juntas
    const bundleId = cartItemRowToRemove.dataset.bundleId;
    /** @type {number[]} */
    let bundleLinesToRemove = [line];

    if (bundleId) {
      const rows = this.refs.cartItemRows;
      for (let i = 0; i < rows.length; i++) {
        if (i !== line - 1 && rows[i].dataset.bundleId === bundleId) {
          bundleLinesToRemove.push(i + 1);
        }
      }
    }

    // Ordenar descendente: eliminar primero la línea más alta para que las demás no se renumeren
    bundleLinesToRemove.sort((a, b) => b - a);

    // Llamar updateQuantity secuencialmente para cada línea del bundle en orden descendente
    // (con await para evitar race conditions donde el servidor re-indexa las líneas)
    for (const bundleLine of bundleLinesToRemove) {
      await this.updateQuantity({
        line: bundleLine,
        quantity: 0,
        action: 'clear',
      });
    }

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      // Get all bundle siblings (garantía extendida)
      ...this.refs.cartItemRows.filter(
        (row) => bundleId && row !== cartItemRowToRemove && row.dataset.bundleId === bundleId
      ),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        void this.#syncProtectedPurchaseFromCart(parsedResponseText);

        this.#updateCartQuantitySelectorButtonStates();
        this.#notifyDrawerAfterMorph();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Notifica al cart-drawer que el section fue reemplazado (morph),
   * para que pueda restaurar el estado abierto del diálogo.
   */
  #notifyDrawerAfterMorph() {
    if (!this.isDrawer) return;
    const cartDrawer = this.closest('cart-drawer-component');
    if (cartDrawer) {
      cartDrawer.dispatchEvent(new CustomEvent('cart:section-morphed', { bubbles: false }));
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      void this.#syncProtectedPurchaseFromCart(event.detail?.resource);
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      void this.#syncProtectedPurchaseFromCart(event.detail?.resource);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#notifyDrawerAfterMorph();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });

      void this.#syncProtectedPurchaseFromCart(event.detail?.resource);
    }
  };

  /**
   * @param {string} rawConditions
   * @returns {Array<{min_price: number, max_price: number, value: number}>}
   */
  #parseProtectedPurchaseConditions(rawConditions) {
    if (!rawConditions) return [];
    try {
      const parsed = JSON.parse(rawConditions);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => ({
          min_price: Number(item?.min_price || 0),
          max_price: Number(item?.max_price || 0),
          value: Number(item?.value || 0),
        }))
        .filter((item) => !Number.isNaN(item.min_price) && !Number.isNaN(item.max_price) && !Number.isNaN(item.value))
        .sort((a, b) => a.min_price - b.min_price);
    } catch (_error) {
      return [];
    }
  }

  /** @returns {string[]} */
  #getCartSectionIds() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionsToUpdate);
  }

  /**
   * @param {Object} params
   * @param {string} params.productId
   * @param {string} params.name
   * @param {number} params.cost
   * @param {string} params.type
   * @param {string} [params.imageUrl]
   * @returns {Promise<string | null>}
   */
  async #ensureProtectedPurchaseVariantId({ productId, name, cost, type, imageUrl }) {
    const cacheKey = `${productId}|${name}|${cost}|${type}`;
    if (this.#addonVariantCache.has(cacheKey)) {
      return this.#addonVariantCache.get(cacheKey);
    }
    try {
      const customerStorageKey = 'kliquea_customer_id';
      const shopify = /** @type {any} */ (window.Shopify);
      const customerId = shopify?.customer?.id
        ? String(shopify.customer.id)
        : (localStorage.getItem(customerStorageKey) || `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);
      localStorage.setItem(customerStorageKey, customerId);

      const response = await fetch('https://ldcok5ribe.execute-api.us-east-1.amazonaws.com/apps/warranty/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          productId,
          warrantyName: name,
          warrantyCost: Number(cost),
          warrantyType: type || 'fixed',
          warrantyImageUrl: imageUrl || '',
          customerId,
          shopId: window.Shopify?.shop || '',
        }),
      });

      if (!response.ok) return null;
      const data = await response.json();
      const variantId = data?.data?.variantId || data?.variantId || data?.variant_id || data?.id;
      if (variantId) {
        const id = String(variantId);
        this.#addonVariantCache.set(cacheKey, id);
        return id;
      }
      return null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Recalcula la Compra Protegida usando subtotal de ítems elegibles
   * y condiciones por rango enviadas en `_Protected_Purchase_Conditions`.
   * @param {any} updatedCart
   */
  async #syncProtectedPurchaseFromCart(updatedCart) {
    const runtime = /** @type {any} */ (window);
    if (runtime.__protectedPurchaseSyncLock) {
      runtime.__protectedPurchaseSyncPending = true;
      return;
    }
    runtime.__protectedPurchaseSyncLock = true;

    try {
      const { cartTotal } = this.refs;
      cartTotal?.shimmer();
      this.refs.cartItemRows?.forEach((row) => {
        const text = row.querySelector('text-component');
        text?.shimmer();
      });
      let currentCart = updatedCart;
      if (!Array.isArray(currentCart?.items)) {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) return;
        currentCart = await cartResponse.json();
      }

      const items = /** @type {any[]} */ (Array.isArray(currentCart?.items) ? currentCart.items : []);
      if (!items.length) return;

      const protectedIndexes = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i]?.properties?._Product_type === 'Protected Purchase') {
          protectedIndexes.push(i);
        }
      }
      const protectedItem = protectedIndexes.length ? items[protectedIndexes[0]] : null;

      const eligibleItems = items.filter((/** @type {any} */ item) => {
        const props = item?.properties || {};
        return props._Protected_Purchase_Eligible === 'true' && props._Product_type !== 'Protected Purchase';
      });

      const sections = this.#getCartSectionIds().join(',');

      // Si no hay elegibles, eliminar cualquier línea existente de compra protegida.
      if (!eligibleItems.length) {
        let lastSections = null;
        for (let i = protectedIndexes.length - 1; i >= 0; i--) {
          const line = protectedIndexes[i] + 1;
          const removeResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
            body: JSON.stringify({
              line,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            }),
          }));
          const removeData = JSON.parse(await removeResponse.text());
          lastSections = removeData.sections;
          this.dispatchEvent(new CartUpdateEvent(removeData, this.sectionId, {
            source: 'cart-items-component',
            sections: removeData.sections,
          }));
        }
        if (lastSections?.[this.sectionId]) {
          morphSection(this.sectionId, lastSections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
        }
        return;
      }

      const conditionsRaw = eligibleItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Conditions)
        ?.properties?._Protected_Purchase_Conditions;
      const conditions = this.#parseProtectedPurchaseConditions(conditionsRaw || '[]');
      if (!conditions.length) return;

      const subtotal = eligibleItems.reduce((/** @type {number} */ sum, /** @type {any} */ item) => {
        const linePriceCents = Number(item?.final_line_price || item?.line_price || 0);
        return sum + (Number.isNaN(linePriceCents) ? 0 : linePriceCents / 100);
      }, 0);

      let selectedCondition = null;
      for (const condition of conditions) {
        if (subtotal >= condition.min_price && subtotal < condition.max_price) {
          selectedCondition = condition;
          break;
        }
      }

      if (!selectedCondition || !(selectedCondition.value > 0)) {
        let lastSections = null;
        for (let i = protectedIndexes.length - 1; i >= 0; i--) {
          const line = protectedIndexes[i] + 1;
          const removeResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
            body: JSON.stringify({
              line,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            }),
          }));
          const removeData = JSON.parse(await removeResponse.text());
          lastSections = removeData.sections;
          this.dispatchEvent(new CartUpdateEvent(removeData, this.sectionId, {
            source: 'cart-items-component',
            sections: removeData.sections,
          }));
        }
        if (lastSections?.[this.sectionId]) {
          morphSection(this.sectionId, lastSections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
        }
        return;
      }

      const addonName =
        eligibleItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Title)?.properties?._Protected_Purchase_Title
        || protectedItem?.properties?._Addon_Name
        || 'Compra Protegida';

      const protectedPurchaseImageUrl =
        eligibleItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Image_URL)?.properties?._Protected_Purchase_Image_URL
        || '';

      const addonVariantId = await this.#ensureProtectedPurchaseVariantId({
        productId: String(eligibleItems[0]?.variant_id || ''),
        name: addonName,
        cost: Number(selectedCondition.value),
        type: protectedItem?.properties?._Addon_Type || 'fixed',
        imageUrl: protectedPurchaseImageUrl,
      });
      if (!addonVariantId) return;

      const articleProperties = /** @type {Record<string, string>} */ ({});
      eligibleItems.forEach((/** @type {any} */ item, /** @type {number} */ index) => {
        const title = item?.product_title || item?.title || `Producto ${item?.id || ''}`;
        const variantTitle = item?.variant_title || '';
        const qty = Number(item?.quantity || 1);
        const label = variantTitle ? `${title} - ${variantTitle} x${qty}` : `${title} x${qty}`;
        articleProperties[`Articulo ${index + 1}`] = label;
      });

      const protectedProperties = {
        _Product_type: 'Protected Purchase',
        _Addon_Name: addonName,
        _Addon_Type: protectedItem?.properties?._Addon_Type || 'fixed',
        _Addon_Cost_Total: Number(selectedCondition.value).toFixed(2),
        _Protected_Purchase_Subtotal: subtotal.toFixed(2),
        ...articleProperties,
      };

      // Eliminar TODAS las líneas actuales de compra protegida para dejar una única línea.
      for (let i = protectedIndexes.length - 1; i >= 0; i--) {
        const line = protectedIndexes[i] + 1;
        await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
          body: JSON.stringify({
            line,
            quantity: 0,
            sections,
            sections_url: window.location.pathname,
          }),
        }));
      }

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: Number(addonVariantId),
            quantity: 1,
            properties: protectedProperties,
          }],
          sections,
        }),
      });

      const addData = JSON.parse(await addResponse.text());
      if (addData?.status) return;

      this.dispatchEvent(new CartUpdateEvent(addData, this.sectionId, {
        source: 'cart-items-component',
        sections: addData.sections,
      }));

      if (addData.sections?.[this.sectionId]) {
        morphSection(this.sectionId, addData.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
      }
    } finally {
      resetShimmer(this);
      runtime.__protectedPurchaseSyncLock = false;
      document.dispatchEvent(new CustomEvent('protected-purchase:sync-complete'));
      if (runtime.__protectedPurchaseSyncPending) {
        runtime.__protectedPurchaseSyncPending = false;
        setTimeout(() => {
          void this.#syncProtectedPurchaseFromCart(undefined);
        }, 0);
      }
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
